import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authAPI } from "../api/endpoints";
import { setOnAuthInvalid } from "../api/axios";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user from storage on mount
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    const stored = localStorage.getItem("user");

    // Validate token is a proper JWT (3 dot-separated segments)
    if (token && token.split(".").length !== 3) {
      console.warn("[Auth] Invalid token in storage, clearing session");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
      setLoading(false);
      return;
    }

    if (token && stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        localStorage.removeItem("user");
      }
    }
    setLoading(false);

    // Listen for auth invalidation from axios interceptor (401/498)
    setOnAuthInvalid(() => setUser(null));
    return () => setOnAuthInvalid(null);
  }, []);

  // Helper: extract token + user from LoginRegisterResponse
  // Response format: { user: { id, token, name, email, profile_image, ... } }
  const saveSession = useCallback((data) => {
    const userData = data.user || data;
    const token = userData.token || data.token;
    localStorage.setItem("auth_token", token);
    // Store user without the token field
    const { token: _, ...userWithoutToken } = userData;
    localStorage.setItem("user", JSON.stringify(userWithoutToken));
    setUser(userWithoutToken);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login(email, password);
    saveSession(res.data);
    return res.data;
  }, [saveSession]);

  const register = useCallback(async (full_name, email, password) => {
    const res = await authAPI.register(full_name, email, password);
    return res.data; // returns { message } — user still needs to verify via email
  }, []);

  const verifyRegistration = useCallback(async (email, verification_code) => {
    const res = await authAPI.verifyRegistration(email, verification_code);
    saveSession(res.data);
    return res.data;
  }, [saveSession]);

  const socialLogin = useCallback(async (full_name, email, provider, profile_image) => {
    const res = await authAPI.socialLogin(full_name, email, provider, profile_image);
    saveSession(res.data);
    return res.data;
  }, [saveSession]);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      // ignore error, clear locally anyway
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.getUser();
      localStorage.setItem("user", JSON.stringify(res.data));
      setUser(res.data);
    } catch {
      // token expired
      logout();
    }
  }, [logout]);

  const isLoggedIn = !!user;

  return (
    <AuthContext.Provider
      value={{ user, isLoggedIn, loading, login, register, verifyRegistration, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
